import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchAirlineLinks, fetchConfig } from "./ConfigActions";
import AppConfigDefault from "../../config/AppConfigDefault";
import AppComponentsDefault from "../../config/AppComponentsDefault";
import CbwsRequestDefaults from "../../config/CbwsRequestDefaults";
import {
  AppComponents,
  AppConfig,
  CbwsRequest,
  MapConfiguration,
  Mapper,
  PopUpMapConfiguration,
} from "../../types";
import { MapperDefault } from "../../config/Mapper";
import {
  MapConfigurationDefault,
  PopUpMapConfigurationDefault,
} from "../../config/Map";
import queryString from "query-string";
import { RootState } from "../../app/store";

interface AirlineLink {
  code: string;
  url: string;
}

export interface ConfigSliceState {
  components: AppComponents;
  app: AppConfig;
  map: MapConfiguration;
  mapper: Mapper;
  popupMap: PopUpMapConfiguration;
  cbwsRequest: CbwsRequest;
  airlineLinks: AirlineLink[];
  loaded: boolean;
  unknown: boolean;
}

const initialState = {
  app: AppConfigDefault,
  mapper: MapperDefault,
  components: AppComponentsDefault,
  map: MapConfigurationDefault,
  popupMap: PopUpMapConfigurationDefault,
  cbwsRequest: CbwsRequestDefaults,
  airlineLinks: [],
  loaded: false,
  unknown: false,
};

export const ConfigSlice = createSlice({
  name: "config",
  initialState: initialState as ConfigSliceState,
  reducers: {
    updateTimeFormat: (state, action: PayloadAction<string>) => {
      state.components.timeFormat = action.payload;
      state.cbwsRequest.timeFormat = action.payload;
    },
    updateLanguage: (state, action: PayloadAction<string>) => {
      state.app = { ...state.app, language: action.payload };
      state.cbwsRequest = {
        ...state.cbwsRequest,
        language: `${action.payload.split("-")[0]}-${state.app.customerId}`,
      };
    },
  },
  extraReducers: {
    [fetchAirlineLinks.fulfilled.toString()]: (state, action) => {
      state.airlineLinks = action.payload;
    },
    [fetchConfig.fulfilled.toString()]: (state, action) => {
      //TODO: simplify so that we don't have properties deep in the main object, which prevent proper deep copy
      const parsed = queryString.parse(window.location.search);
      state.app = { ...state.app, ...action.payload.AppConfigCustom };
      const airportCode = parsed["airport"] || parsed["?airport"];
      if (airportCode) {
        state.app.customerType.airline = false;
        state.app.customerType.airport = true;
        state.app.customerType.airportVersion = {
          ...state.app.customerType.airportVersion,
          airport: `${airportCode}`,
          single: !Array.isArray(airportCode),
          multi: Array.isArray(airportCode)
        };
      }
      const lfMain = parsed["lfMain"] || parsed["?lfMain"];
      if (lfMain) {
        //@ts-ignore
        state.app = { ...state.app, lfMain: `${lfMain}` };
      }
      state.mapper = { ...state.mapper, ...action.payload.MapperCustom };
      state.components = {
        ...state.components,
        ...action.payload.AppComponentsCustom,
        Results: {
          ...state.components.Results,
          ...action.payload.AppComponentsCustom.Results,
        },
      };
      state.map = { ...state.map, ...action.payload.MapConfigurationCustom };
      state.popupMap = {
        ...state.popupMap,
        ...action.payload.PopUpMapConfigurationCustom,
      };
      state.cbwsRequest = {
        ...state.cbwsRequest,
        ...action.payload.CbwsRequestDefaultsCustom,
      };
      let lng = parsed["lng"] || parsed["?lng"];
      if (lng && typeof lng == "string") {
        //@ts-ignore
        state.app = { ...state.app, language: lng };
        state.cbwsRequest = {
          ...state.cbwsRequest,
          language: `${lng.split("-")[0]}-${state.app.customerId}`,
        };
      }
      
      const airlineCode = parsed["airline"] || parsed["?airline"];
      if (airlineCode) {
        state.app.customerType.airlineVersion = {
          ...state.app.customerType.airlineVersion,
          airline: `${airlineCode}`,
          single: true
        };
        state.app.customerType = {
          ...state.app.customerType,
          airline: true,
          airport: false
        }
        state.app.customerType.airportVersion = {
          ...state.app.customerType.airportVersion,
          airport: null,
          single: false
        };
        state.mapper.flightFinder = {
          ...state.mapper.flightFinder,
          directs: {
            enable: state.mapper.flightFinder.directs.enable,
            carrier1Criteria: `${airlineCode}`,
            carrier2Criteria: `${airlineCode}`,
            carrier3Criteria: state.mapper.flightFinder.directs.carrier3Criteria,
            prefferedCarrier: `${airlineCode}`,
            preferredCarrierCriteria: `${airlineCode}`,
            originCriteria: state.mapper.flightFinder.directs.originCriteria,
            destinationCriteria: state.mapper.flightFinder.directs.destinationCriteria,
            viaCriteria: state.mapper.flightFinder.directs.viaCriteria
          },
          connections: {
            enable: state.mapper.flightFinder.connections.enable,
            carrier1Criteria: `${airlineCode}`,
            carrier2Criteria: `${airlineCode}`,
            carrier3Criteria: null,
            prefferedCarrier: `${airlineCode}`,
            preferredCarrierCriteria: `${airlineCode}`,
            originCriteria: state.mapper.flightFinder.connections.originCriteria,
            destinationCriteria: state.mapper.flightFinder.connections.destinationCriteria,
            viaCriteria: state.mapper.flightFinder.connections.viaCriteria
          }
        }

        state.mapper = {
          ...state.mapper,
          originPort: null
        }

        state.cbwsRequest = {
          ...state.cbwsRequest,
          carrier1Criteria: `${airlineCode}`,
          preferredCarrierCriteria: `${airlineCode}`,
          originCriteria: '***'
        };
      }
      state.loaded = true;
    },
    [fetchConfig.rejected.toString()]: (state, action) => {
      state.loaded = true;
      state.unknown = true;
    },
  },
});

export const { updateTimeFormat, updateLanguage } = ConfigSlice.actions;

export const selectConfig = (state: RootState) => state.config;

export const selectAppConfig = (state: RootState) => state.config.app;

export const selectAirlineLinks = (state: RootState) =>
  state.config.airlineLinks;

export default ConfigSlice.reducer;
